import { Controller } from "@hotwired/stimulus"
import { Calendar } from "vanilla-calendar-pro"
import "vanilla-calendar-pro/styles/index.css"
import "vanilla-calendar-pro/styles/themes/light.css"
import "vanilla-calendar-pro/styles/themes/dark.css"

/**
 * Shared date range picker controller — used by both the nav date picker
 * (dark theme, popover) and the bid calculator wizard (light theme, inline).
 *
 * Manages preset buttons, vanilla-calendar-pro sync, and hidden field updates.
 * Dispatches "changed" event when the date range changes so parent controllers
 * can react (e.g. update display text, validate wizard steps).
 */
export default class extends Controller {
  static targets = ["dateFrom", "dateTo", "rangeMode", "rangePreset", "calendarContainer", "preset", "compareKind", "compareCustom", "compareYears"]

  connect() {
    if (!this.hasCalendarContainerTarget) return

    const theme = this.calendarContainerTarget.dataset.theme || "light"
    const maxDate = this.calendarContainerTarget.dataset.maxDate
    // Absent everywhere except the experiments detail page, where the range can't run outside the
    // experiment's own window.
    const minDate = this.calendarContainerTarget.dataset.minDate
    const from = this.dateFromTarget.value
    const to = this.dateToTarget.value

    // Parse start date to set initial visible month
    const startDate = new Date(from + "T00:00:00")

    this.calendar = new Calendar(this.calendarContainerTarget, {
      type: "multiple",
      displayMonthsCount: 2,
      selectionDatesMode: "multiple-ranged",
      selectedDates: from && to ? [from, to] : [],
      selectedMonth: startDate.getMonth(),
      selectedYear: startDate.getFullYear(),
      dateMax: maxDate || "today",
      ...(minDate ? {dateMin: minDate} : {}),
      firstWeekday: 0,
      displayDatesOutside: false,
      selectedTheme: theme,
      onClickDate: (self) => this.#dateSelected(self),
    })
    this.calendar.init()

    if (this.calendarContainerTarget.dataset.inactiveWeekends === "true") {
      this.calendarContainerTarget.classList.add("vc-inactive-weekends")
    }
  }

  disconnect() {
    this.calendar?.destroy()
  }

  // --- Actions ---

  // Each button carries its range, resolved on the server by V2::DateRangePreset against the
  // calendar's max date, so the browser does no date arithmetic here.
  selectPreset(event) {
    event.preventDefault()
    const startStr = event.currentTarget.dataset.from
    const endStr = event.currentTarget.dataset.to
    const start = new Date(startStr + "T00:00:00")

    this.dateFromTarget.value = startStr
    this.dateToTarget.value = endStr
    // "Last quarter" carries a mode that shapes the server's "Previous period"; the other presets
    // carry none.
    this.#setRangeMode(event.currentTarget.dataset.mode || "")
    this.#setRangePreset(event.currentTarget.dataset.preset || "")
    this.#replacePreviousPeriod(event.currentTarget.dataset.replacesPreviousPeriodWith)

    if (this.calendar) {
      this.calendar.set(
        {
          selectedDates: [startStr, endStr],
          selectedMonth: start.getMonth(),
          selectedYear: start.getFullYear(),
        },
        { dates: true, month: true, year: true },
      )
      this.calendar.update({ dates: true, month: true, year: true })
    }

    this.presetTargets.forEach((btn) => btn.classList.remove("active"))
    event.currentTarget.classList.add("active")

    this.#dispatchChanged(startStr, endStr)
  }

  apply(event) {
    event.preventDefault()
    this.element.closest("form")?.requestSubmit()
  }

  // Reveal the custom range for "Custom" and the year-count for "Year over year".
  compareKindChanged() {
    const kind = this.compareKindTarget.value
    if (this.hasCompareCustomTarget) this.compareCustomTarget.classList.toggle("hidden", kind !== "custom")
    if (this.hasCompareYearsTarget) this.compareYearsTarget.classList.toggle("hidden", kind !== "previous_year")
  }

  // --- Helpers ---

  formatDisplay(isoStr) {
    const d = new Date(isoStr + "T00:00:00")
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  // --- Private ---

  #dateSelected(self) {
    const dates = self.context.selectedDates
    if (!dates || dates.length === 0) return

    if (dates.length >= 2) {
      const sorted = [...dates].filter((d) => this.#isISODate(d)).sort()
      if (sorted.length < 2) return

      const from = sorted[0]
      const to = sorted[sorted.length - 1]

      this.dateFromTarget.value = from
      this.dateToTarget.value = to
      // A hand-picked range is an ordinary one, even when its dates match a preset's.
      this.#setRangeMode("")
      this.#setRangePreset("")

      this.presetTargets.forEach((btn) => btn.classList.remove("active"))
      this.#dispatchChanged(from, to)
    }
  }

  // "YTD" is read against the same dates last year, so it moves a "Previous period" comparison to
  // "Year over year" where the reader can see it before applying. "No comparison" and "Custom" were
  // chosen deliberately and stay.
  #replacePreviousPeriod(kind) {
    if (!kind || !this.hasCompareKindTarget) return
    if (this.compareKindTarget.value !== "previous_period") return

    this.compareKindTarget.value = kind
    this.compareKindChanged()
  }

  #setRangeMode(mode) {
    if (this.hasRangeModeTarget) this.rangeModeTarget.value = mode
  }

  #setRangePreset(key) {
    if (this.hasRangePresetTarget) this.rangePresetTarget.value = key
  }

  #isISODate(value) {
    return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
  }

  #dispatchChanged(from, to) {
    this.dispatch("changed", {
      detail: { from, to },
      bubbles: true,
    })
  }
}
